<template>
  <div class="vendor-payments-list-container vendor-payments-list-main vendor-payments-list-page--glass glass-tab-page">
    <BrucdcumHeader :breadcrumb="['Company', 'Vendors', 'Vendor Payments']" />

    <div class="glass-tab-view glass-tab-view--flush vendor-payments-list-glass-shell">
      <div class="glass-tab-view__content vendor-payments-list-tab-content vendor-payments-list-page-stack">
        <div class="vendor-payments-toolbar vendor-payments-toolbar-glass glass-tab-panel--nested">
          <div class="vendor-payments-list-header-actions">
            <SecondaryButton
              label="Export to CSV"
              icon="bi bi-download"
              @click="handleExportCSV"
            />
          </div>
          <div class="vendor-payments-list-filters vendor-payments-list-filters-row">
            <div class="vendor-payments-list-filter-field vendor-payments-list-filter-field--view">
              <LabelDropdown
                label="Current View"
                select-id="vendor-payments-list-current-view"
                v-model="currentView"
                inline
                flex
                :searchable="false"
                :options="currentViewOptions"
              />
            </div>
            <div class="vendor-payments-list-filter-field vendor-payments-list-filter-field--date-type">
              <LabelDropdown
                label="Date"
                select-id="vendor-payments-list-date-type"
                v-model="dateType"
                inline
                flex
                :searchable="false"
                :options="dateTypeOptions"
              />
            </div>
            <div class="vendor-payments-list-filter-field vendor-payments-list-filter-field--date-range">
              <LabelDropdown
                label="Date Range"
                select-id="vendor-payments-list-date-range"
                v-model="dateRange"
                inline
                flex
                :searchable="false"
                :options="dateRangeOptions"
                @change="onDateRangeChange"
              />
            </div>
            <div class="vendor-payments-list-filter-field vendor-payments-list-filter-field--date">
              <LabelDateField
                input-id="vendor-payments-list-from-date"
                label="From"
                v-model="fromDate"
                placeholder="MM/DD/YYYY"
                flex
                :readonly="dateRange !== 'Custom'"
              />
            </div>
            <div class="vendor-payments-list-filter-field vendor-payments-list-filter-field--date">
              <LabelDateField
                input-id="vendor-payments-list-to-date"
                label="To"
                v-model="toDate"
                placeholder="MM/DD/YYYY"
                flex
                :readonly="dateRange !== 'Custom'"
              />
            </div>
            <div class="vendor-payments-list-filter-update">
              <PrimaryButton
                label="Update"
                icon="bi bi-arrow-clockwise"
                compact
                @click="handleUpdate"
              />
            </div>
          </div>
        </div>

        <div class="vendor-payments-list-table-panel glass-tab-panel--nested">
          <div class="vendor-payments-table-section consultant-glass-table-section">
            <div class="consultant-glass-table-responsive consultant-glass-table-responsive--framed">
              <div class="consultant-glass-table-scroll-x">
      <table class="consultant-glass-table vendor-payments-list-table">
        <thead>
          <tr>
            <th class="vendor-payments-expand-icon-column"></th>
            <th>Pay Date</th>
            <th>Pay No.</th>
            <th>Status</th>
            <th>Vendor Name</th>
            <th>Payment Method</th>
            <th>Pay From Account</th>
            <th>Amount Paid</th>
            <th>Comments</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="9" class="consultant-glass-table-no-records">Loading...</td>
          </tr>
          <tr v-else-if="error">
            <td colspan="9" class="consultant-glass-table-no-records">{{ error }}</td>
          </tr>
          <tr v-else-if="paginatedPayments.length === 0">
            <td colspan="9" class="consultant-glass-table-no-records">No records to show</td>
          </tr>
          <template v-for="(payment, idx) in paginatedPayments" :key="`${payment.paymentSourceType}-${payment.id || idx}`">
            <tr
              class="consultant-glass-table-row vendor-payments-payment-row"
              :data-payment-id="payment.id"
              :data-payment-source="payment.paymentSourceType"
              @click="toggleDetails(payment)"
              :class="{
                'clickable': true,
                'vendor-payments-payment-row-highlighted': isRouteHighlightedPayment(payment)
              }"
            >
              <td class="vendor-payments-expand-icon">
                <i :class="payment.showDetails ? 'bi bi-chevron-down' : 'bi bi-chevron-right'"></i>
              </td>
              <td>{{ formatDateToUS(payment.date) }}</td>
              <td>{{ payment.no }}</td>
              <td>{{ displayPaymentStatus(payment) }}</td>
              <td>{{ getVendorName(payment) }}</td>
              <td>{{ getPaymentMethod(payment) }}</td>
              <td>{{ getAccountName(payment) }}</td>
              <td
                class="vendor-payments-list-amount-cell"
                :class="{ 'vendor-payments-list-amount-cell--empty': !payment.total }"
              >{{ formatAmount(payment.total) }}</td>
              <td>{{ payment.comments || '-' }}</td>
            </tr>
            <!-- Payment Details Row -->
            <tr v-if="payment.showDetails" class="vendor-payments-details-row">
              <td colspan="9" class="vendor-payments-details-container">
                <div v-if="payment.loadingDetails" class="vendor-payments-loading-details">
                  Loading details...
                </div>
                <div v-else-if="payment.details" class="vendor-payments-details-content">
                  <template v-if="isDirectExpensePayment(payment)">
                    <div v-if="payment.details.accountRecords && payment.details.accountRecords.length > 0" class="vendor-payments-details-section">
                      <h4>Account Records</h4>
                      <table class="vendor-payments-details-table">
                        <thead>
                          <tr>
                            <th>Account</th>
                            <th>Description</th>
                            <th>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(account, accIdx) in payment.details.accountRecords" :key="`direct-account-${accIdx}`">
                            <td>{{ formatDirectExpenseAccount(account) }}</td>
                            <td>{{ account.displayDescription || account.description || '-' }}</td>
                            <td>{{ formatAmount(account.amount) }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div v-if="payment.details.itemRecords && payment.details.itemRecords.length > 0" class="vendor-payments-details-section">
                      <h4>Item Records</h4>
                      <table class="vendor-payments-details-table">
                        <thead>
                          <tr>
                            <th>Item</th>
                            <th>Description</th>
                            <th>Quantity</th>
                            <th>Unit Price</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(item, itemIdx) in payment.details.itemRecords" :key="`direct-item-${itemIdx}`">
                            <td>{{ item.itemName || '-' }}</td>
                            <td>{{ item.displayDescription || item.description || item.itemName || '-' }}</td>
                            <td>{{ item.quantity || 0 }}</td>
                            <td>{{ formatAmount(item.unitPrice) }}</td>
                            <td>{{ formatAmount(item.total) }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div
                      v-if="!payment.details.accountRecords?.length && !payment.details.itemRecords?.length"
                      class="vendor-payments-no-details"
                    >
                      No line details found for this expense.
                    </div>
                  </template>
                  <template v-else>
                  <!-- Paid Bills -->
                  <div v-if="payment.details.payBillRecords && payment.details.payBillRecords.length > 0" class="vendor-payments-details-section">
                    <h4>Paid Bills</h4>
                    <table class="vendor-payments-details-table">
                      <thead>
                        <tr>
                          <th class="vendor-payments-expand-icon-column"></th>
                          <th>Bill No</th>
                          <th>Bill Date</th>
                          <th>Bill Total</th>
                          <th>Amount Paid</th>
                          <th>Payment Method</th>
                          <th>Payment Account</th>
                          <th>Bill Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        <template v-for="(payRecord, payIdx) in payment.details.payBillRecords" :key="`pay-bill-${payRecord.billId || payIdx}`">
                          <tr class="vendor-payments-paid-bill-row" @click.stop="togglePaidBillDetails(payRecord)" :class="{ 'clickable': true }">
                            <td class="vendor-payments-expand-icon">
                              <i :class="payRecord.showDetails ? 'bi bi-chevron-down' : 'bi bi-chevron-right'"></i>
                            </td>
                            <td>{{ payRecord.bill?.billNo || '-' }}</td>
                            <td>{{ formatDateToUS(payRecord.bill?.date) }}</td>
                            <td>{{ formatAmount(payRecord.bill?.total) }}</td>
                            <td>{{ formatAmount(payRecord.amountPaid) }}</td>
                            <td>{{ payment.paymentMethod || '-' }}</td>
                            <td>{{ formatPayFromAccount(payment.payFromAccount) }}</td>
                            <td>
                              <span :class="['vendor-payments-status-badge', payRecord.bill?.status ? `status-${payRecord.bill.status.toLowerCase().replace(' ', '-')}` : '']">
                                {{ payRecord.bill?.status || '-' }}
                              </span>
                            </td>
                          </tr>
                          <!-- Paid Bill Details Row -->
                          <tr v-if="payRecord.showDetails" class="vendor-payments-paid-bill-details-row">
                            <td colspan="8" class="vendor-payments-details-container">
                              <div v-if="payRecord.loadingDetails" class="vendor-payments-loading-details">
                                Loading details...
                              </div>
                              <div v-else-if="payRecord.billDetails" class="vendor-payments-details-content">
                                <!-- Account Records -->
                                <div v-if="payRecord.billDetails.accountRecords && payRecord.billDetails.accountRecords.length > 0" class="vendor-payments-details-section">
                                  <h4>Account Records</h4>
                                  <table class="vendor-payments-details-table">
                                    <thead>
                                      <tr>
                                        <th>Account</th>
                                        <th>Description</th>
                                        <th>Amount</th>
                                        <th>Paid Amount</th>
                                        <th>Status</th>
                                        <th>Type</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr v-for="(account, accIdx) in payRecord.billDetails.accountRecords" :key="`account-${payIdx}-${accIdx}`">
                                        <td>{{ formatAccountName(account) }}</td>
                                        <td>{{ account.description || '-' }}</td>
                                        <td>{{ formatAmount(account.amount) }}</td>
                                        <td>{{ formatAmount(account.paidAmount) }}</td>
                                        <td>
                                          <span :class="['vendor-payments-status-badge', account.status ? `status-${account.status.toLowerCase().replace(' ', '-')}` : 'status-not-paid']">
                                            {{ account.status || 'Not Paid' }}
                                          </span>
                                        </td>
                                        <td>
                                          <span v-if="account.isManualRecord" class="vendor-payments-type-badge manual">Manual</span>
                                          <span v-else class="vendor-payments-type-badge auto">Auto</span>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                                <!-- Item Records -->
                                <div v-if="payRecord.billDetails.itemRecords && payRecord.billDetails.itemRecords.length > 0" class="vendor-payments-details-section">
                                  <h4>Item Records</h4>
                                  <table class="vendor-payments-details-table">
                                    <thead>
                                      <tr>
                                        <th>Description</th>
                                        <th>Quantity</th>
                                        <th>Unit Price</th>
                                        <th>Total</th>
                                        <th>Paid Amount</th>
                                        <th>Status</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr v-for="(item, itemIdx) in payRecord.billDetails.itemRecords" :key="`item-${payIdx}-${itemIdx}`">
                                        <td>{{ item.description || '-' }}</td>
                                        <td>{{ item.quantity || 0 }}</td>
                                        <td>{{ formatAmount(item.unitPrice) }}</td>
                                        <td>{{ formatAmount(item.total) }}</td>
                                        <td>{{ formatAmount(item.paidAmount) }}</td>
                                        <td>
                                          <span :class="['vendor-payments-status-badge', item.status ? `status-${item.status.toLowerCase().replace(' ', '-')}` : 'status-not-paid']">
                                            {{ item.status || 'Not Paid' }}
                                          </span>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                                <!-- Consultant Records -->
                                <div v-if="payRecord.billDetails.consultantRecords && payRecord.billDetails.consultantRecords.length > 0" class="vendor-payments-details-section">
                                  <h4>Consultant Records</h4>
                                  <table class="vendor-payments-details-table">
                                    <thead>
                                      <tr>
                                        <th>Consultant Name</th>
                                        <th>Description</th>
                                        <th>Hours</th>
                                        <th>Hourly Rate</th>
                                        <th>Total</th>
                                        <th>Paid Amount</th>
                                        <th>Status</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr v-for="(consultant, consIdx) in payRecord.billDetails.consultantRecords" :key="`consultant-${payIdx}-${consIdx}`">
                                        <td>{{ consultant.consultantName || '-' }}</td>
                                        <td>{{ consultant.description || '-' }}</td>
                                        <td>{{ consultant.hours || 0 }}</td>
                                        <td>{{ formatAmount(consultant.hourlyRate) }}</td>
                                        <td>{{ formatAmount(consultant.total) }}</td>
                                        <td>{{ formatAmount(consultant.paidAmount) }}</td>
                                        <td>
                                          <span :class="['vendor-payments-status-badge', consultant.status ? `status-${consultant.status.toLowerCase().replace(' ', '-')}` : 'status-not-paid']">
                                            {{ consultant.status || 'Not Paid' }}
                                          </span>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                                <div v-if="!payRecord.billDetails.accountRecords?.length && !payRecord.billDetails.itemRecords?.length && !payRecord.billDetails.consultantRecords?.length" class="vendor-payments-no-details">
                                  No detailed records available for this bill.
                                </div>
                              </div>
                            </td>
                          </tr>
                        </template>
                      </tbody>
                    </table>
                  </div>
                  <div v-if="!payment.details.payBillRecords?.length" class="vendor-payments-no-details">
                    No paid bills found for this payment.
                  </div>
                  </template>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
              </div>
            </div>
          </div>

          <div class="vendor-payments-list-pagination-wrapper">
            <PaginatorComponent
              v-if="filteredPayments.length >= 2000"
              :current-page="currentPage"
              :total-count="filteredPayments.length"
              :records-per-page="itemsPerPage"
              :loading="loading"
              @page-changed="handlePageChange"
            />
            <PaginationComponent
              v-else
              select-id="vendor-payments-list-items-per-page"
              :current-page="currentPage"
              :total-items="filteredPayments.length"
              :items-per-page="itemsPerPage"
              item-label="payments"
              inline
              :searchable="false"
              :show-items-per-page="true"
              @page-change="handlePageChange"
              @items-per-page-change="handleItemsPerPageChange"
            />
          </div>
        </div>
      </div>
    </div>

    <VendorDetailsSearch v-if="showSearchModal" @close="showSearchModal = false" />
  </div>
</template>

<script>
import '../styles/VendorPaymentsList.css';
import VendorDetailsSearch from '@/modules/vendor/views/VendorDetailsSearch.vue';
import logger from '@/utils/logger';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import PaginationComponent from '@/components/PaginationComponent.vue';
import PaginatorComponent from '@/components/PaginatorComponent.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import LabelDateField from '@/modules/consultant/components/LabelDateField.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';
import ApiService from '@/services/api.service.js';
import {
  formatReportDateUS,
  localDateToISO,
  localTodayISO,
  toReportDateISO,
} from '@/modules/reports/utils/reportCalendarDate.js';

const DATE_RANGE_OPTIONS = Object.freeze([
  { label: 'All', value: 'All' },
  { label: 'This week', value: 'This week' },
  { label: 'This month', value: 'This month' },
  { label: 'Last week', value: 'Last week' },
  { label: 'Last month', value: 'Last month' },
  { label: 'This financial year', value: 'This financial year' },
  { label: 'Last financial year', value: 'Last financial year' },
  { label: 'This financial quarter', value: 'This financial quarter' },
  { label: 'Last financial quarter', value: 'Last financial quarter' },
  { label: 'Financial year to date', value: 'Financial year to date' },
  { label: 'Custom', value: 'Custom' },
]);

export default {
  name: 'VendorPaymentsList',
  components: {
    VendorDetailsSearch,
    BrucdcumHeader,
    PaginationComponent,
    PaginatorComponent,
    LabelDropdown,
    LabelDateField,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      payments: [],
      vendors: [],
      accountsMap: {},
      loading: false,
      error: '',
      currentView: 'All',
      dateType: 'All',
      dateRange: 'All',
      fromDate: '',
      toDate: '',
      appliedDateType: 'All',
      appliedFromDate: '',
      appliedToDate: '',
      dateRangeOptions: DATE_RANGE_OPTIONS,
      currentPage: 1,
      itemsPerPage: 10,
      showSearchModal: false,
      highlightedVendorPayBillId: null,
      hasAppliedVendorPaymentRouteFocus: false,
    };
  },
  async created() {
    await this.fetchPayments();
  },
  computed: {
    currentViewOptions() {
      return [
        { value: 'Not issued', label: 'Not issued' },
        { value: 'Issued', label: 'Issued' },
        { value: 'All', label: 'All' },
      ];
    },
    dateTypeOptions() {
      return [
        { value: 'All', label: 'All' },
        { value: 'Pay Date', label: 'Pay Date' },
        { value: 'Issue Date', label: 'Issue Date' },
      ];
    },
    filteredPayments() {
      let filtered = this.payments;
      logger.log('Initial payments:', filtered.length);
      
      // Filter by status (convert VendorPayBill data to match UI expectations)
      if (this.currentView !== 'All') {
        filtered = filtered.filter(payment => {
          // Map VendorPayBill status to UI status
          const status = this.getPaymentStatus(payment);
          logger.log('Payment status:', status, 'Current view:', this.currentView);
          return status === this.currentView;
        });
        logger.log('After status filter:', filtered.length);
      }
      
      let fromIso = toReportDateISO(this.appliedFromDate);
      let toIso = toReportDateISO(this.appliedToDate);
      if (fromIso && toIso && fromIso > toIso) {
        const swap = fromIso;
        fromIso = toIso;
        toIso = swap;
      }

      if (fromIso || toIso) {
        filtered = filtered.filter((payment) => {
          const paymentIso = this.getFilterDateIso(payment, this.appliedDateType);
          if (!paymentIso) return false;
          if (fromIso && paymentIso < fromIso) return false;
          if (toIso && paymentIso > toIso) return false;
          return true;
        });
      }
      logger.log('Final filtered payments:', filtered.length);
      return filtered;
    },
    paginatedPayments() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredPayments.slice(start, start + this.itemsPerPage);
    }
  },
  methods: {
    toDisplayDate(date) {
      if (!date) return '';
      const iso = typeof date === 'string' ? toReportDateISO(date) : localDateToISO(date);
      return formatReportDateUS(iso);
    },
    getFilterDateIso(payment, dateType = 'All') {
      if (dateType === 'Issue Date') {
        return toReportDateISO(payment?.printDate || payment?.date);
      }
      return toReportDateISO(payment?.date);
    },
    onDateRangeChange() {
      const today = new Date();
      let from = '';
      let to = '';
      const getMonday = (d) => {
        const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const day = date.getDay();
        const diff = date.getDate() - day + (day === 0 ? -6 : 1);
        return new Date(date.getFullYear(), date.getMonth(), diff);
      };
      const getFinancialYearStart = (d) => {
        const year = d.getMonth() < 3 ? d.getFullYear() - 1 : d.getFullYear();
        return new Date(year, 3, 1);
      };
      const getFinancialYearEnd = (d) => {
        const year = d.getMonth() < 3 ? d.getFullYear() : d.getFullYear() + 1;
        return new Date(year, 2, 31);
      };
      const getQuarterStart = (d) => {
        const month = d.getMonth();
        const quarterStartMonth = month - (month % 3);
        return new Date(d.getFullYear(), quarterStartMonth, 1);
      };
      const getQuarterEnd = (d) => {
        const month = d.getMonth();
        const quarterEndMonth = month - (month % 3) + 2;
        return new Date(d.getFullYear(), quarterEndMonth + 1, 0);
      };

      switch (this.dateRange) {
        case 'All':
          from = '';
          to = '';
          break;
        case 'This week':
          from = this.toDisplayDate(getMonday(today));
          to = this.toDisplayDate(today);
          break;
        case 'Last week': {
          const lastWeekAnchor = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
          const lastWeekMonday = getMonday(lastWeekAnchor);
          from = this.toDisplayDate(lastWeekMonday);
          to = this.toDisplayDate(
            new Date(
              lastWeekMonday.getFullYear(),
              lastWeekMonday.getMonth(),
              lastWeekMonday.getDate() + 6
            )
          );
          break;
        }
        case 'This month':
          from = this.toDisplayDate(new Date(today.getFullYear(), today.getMonth(), 1));
          to = this.toDisplayDate(new Date(today.getFullYear(), today.getMonth() + 1, 0));
          break;
        case 'Last month': {
          const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          from = this.toDisplayDate(new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 1));
          to = this.toDisplayDate(new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0));
          break;
        }
        case 'This financial year':
          from = this.toDisplayDate(getFinancialYearStart(today));
          to = this.toDisplayDate(getFinancialYearEnd(today));
          break;
        case 'Last financial year': {
          const lastYear = new Date(getFinancialYearStart(today));
          lastYear.setFullYear(lastYear.getFullYear() - 1);
          from = this.toDisplayDate(getFinancialYearStart(lastYear));
          to = this.toDisplayDate(getFinancialYearEnd(lastYear));
          break;
        }
        case 'This financial quarter':
          from = this.toDisplayDate(getQuarterStart(today));
          to = this.toDisplayDate(getQuarterEnd(today));
          break;
        case 'Last financial quarter': {
          const lastQuarter = new Date(getQuarterStart(today));
          lastQuarter.setMonth(lastQuarter.getMonth() - 3);
          from = this.toDisplayDate(getQuarterStart(lastQuarter));
          to = this.toDisplayDate(getQuarterEnd(lastQuarter));
          break;
        }
        case 'Financial year to date':
          from = this.toDisplayDate(getFinancialYearStart(today));
          to = formatReportDateUS(localTodayISO());
          break;
        case 'Custom':
          return;
        default:
          break;
      }

      this.fromDate = from;
      this.toDate = to;
      if (this.dateRange !== 'Custom') {
        this.handleUpdate();
      }
    },
    isRouteHighlightedPayment(payment) {
      return (
        this.highlightedVendorPayBillId &&
        payment &&
        String(payment.id) === String(this.highlightedVendorPayBillId)
      );
    },
    focusVendorPaymentFromRoute() {
      const vendorPayBillId = this.$route?.query?.vendorPayBillId;
      if (!vendorPayBillId) return;

      this.highlightedVendorPayBillId = String(vendorPayBillId);
      this.currentView = 'All';
      this.dateType = 'All';
      this.dateRange = 'All';
      this.fromDate = '';
      this.toDate = '';
      this.appliedDateType = 'All';
      this.appliedFromDate = '';
      this.appliedToDate = '';

      this.$nextTick(() => {
        const targetIndex = this.filteredPayments.findIndex(
          payment => String(payment?.id) === String(this.highlightedVendorPayBillId)
        );
        if (targetIndex < 0) return;

        this.currentPage = Math.floor(targetIndex / this.itemsPerPage) + 1;
        this.$nextTick(() => {
          const targetRow = document.querySelector(`tr[data-payment-id="${this.highlightedVendorPayBillId}"]`);
          if (targetRow) {
            targetRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
            this.hasAppliedVendorPaymentRouteFocus = true;
          }
        });
      });
    },
    async fetchPayments() {
      this.loading = true;
      this.error = '';
      try {
        const companyId = this.$route.params.id;
        if (!companyId) {
          this.error = 'Company ID is required';
          return;
        }

        const [vendors, accountsResponse] = await Promise.all([
          ApiService.getAllVendors(companyId),
          ApiService.getCompanyAccounts(companyId)
            .then((res) => res.data?.accounts || res.data || [])
            .catch(() => []),
        ]);

        logger.log('Found vendors:', vendors.length);
        this.vendors = vendors;
        this.accountsMap = this.buildAccountsMap(accountsResponse);

        const allPayBills = [];
        for (const vendor of vendors) {
          try {
            const payBills = await ApiService.get(`/vendor/pay-bill/vendor/${vendor.id}`).then(res => res.data);
            logger.log(`Pay Bills for vendor ${vendor.vendorName}:`, payBills.length);

            payBills.forEach(payBill => {
              const processedPayBill = payBill.toJSON ? payBill.toJSON() : payBill;

              if (processedPayBill.records) {
                processedPayBill.records = processedPayBill.records.map(record => {
                  return record.toJSON ? record.toJSON() : record;
                });
              }

              allPayBills.push(processedPayBill);
            });
          } catch (vendorError) {
            logger.warn(`Failed to fetch pay bills for vendor ${vendor.id}:`, vendorError);
          }
        }

        let directExpenses = [];
        try {
          const expenseRows = await ApiService.getBillsAndExpenses(companyId, { view: 'Expenses' });
          directExpenses = (Array.isArray(expenseRows) ? expenseRows : [])
            .filter((row) =>
              row?.expenseKind === 'Cash Expense'
              || row?.expenseKind === 'Credit Card Expense'
            );
        } catch (expenseError) {
          logger.warn('Failed to fetch direct vendor expenses:', expenseError);
        }

        logger.log('Total pay bills fetched:', allPayBills.length);
        logger.log('Total direct expenses fetched:', directExpenses.length);

        const payBillPayments = allPayBills.map((payment) => ({
          ...payment,
          paymentSourceType: 'pay-bill',
          showDetails: false,
          loadingDetails: false,
          details: null,
          records: payment.records || [],
        }));

        const directExpensePayments = directExpenses.map((row) => this.mapDirectExpenseToPayment(row));

        this.payments = [...payBillPayments, ...directExpensePayments]
          .sort((a, b) => new Date(b.date) - new Date(a.date));

        this.focusVendorPaymentFromRoute();
        this.error = '';
      } catch (e) {
        logger.error('Error fetching vendor pay bills:', e);
        this.error = 'Failed to load vendor payments.';
        this.payments = [];
      } finally {
        this.loading = false;
      }
    },
    buildAccountsMap(accounts) {
      const list = Array.isArray(accounts) ? accounts : [];
      return list.reduce((map, account) => {
        if (account?.id != null) {
          map[account.id] = account;
        }
        return map;
      }, {});
    },
    mapDirectExpenseToPayment(row) {
      const paymentSourceType = row.expenseKind === 'Cash Expense'
        ? 'cash-expense'
        : 'credit-card-expense';
      const payFromAccount = row.payFrom ? this.accountsMap[row.payFrom] : null;

      return {
        id: row.id,
        paymentSourceType,
        expenseKind: row.expenseKind,
        date: row.date,
        no: row.no,
        status: 'Paid',
        vendorId: row.vendorId,
        vendorName: row.vendorName,
        paymentMethod: paymentSourceType === 'cash-expense' ? 'Cash' : 'Credit Card',
        payFrom: row.payFrom,
        payFromAccount: payFromAccount || null,
        total: row.originalAmount ?? row.paidAmount ?? row.total,
        comments: row.comments || '',
        showDetails: false,
        loadingDetails: false,
        details: null,
      };
    },
    isDirectExpensePayment(payment) {
      return payment?.paymentSourceType === 'cash-expense'
        || payment?.paymentSourceType === 'credit-card-expense';
    },
    getPaymentStatus(payment) {
      if (this.isDirectExpensePayment(payment)) {
        return 'Issued';
      }
      return payment.status || 'Issued';
    },
    displayPaymentStatus(payment) {
      if (this.isDirectExpensePayment(payment)) {
        return 'Paid';
      }
      return this.getPaymentStatus(payment);
    },
    formatAmount(amount) {
      return amount ? `$${Number(amount).toFixed(2)}` : '-';
    },
    formatDate(date) {
      return date ? new Date(date).toLocaleDateString() : '-';
    },
    formatDateToUS(dateString) {
      if (!dateString || !dateString.trim()) return '-';
      
      try {
        // Parse the date (handles ISO format, timestamps, etc.)
        const date = new Date(dateString);
        
        // Check if date is valid
        if (isNaN(date.getTime())) return dateString;
        
        // Format as MM/DD/YYYY
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        
        return `${month}/${day}/${year}`;
      } catch (error) {
        logger.error('Error formatting date:', error);
        return dateString;
      }
    },
    async toggleDetails(payment) {
      payment.showDetails = !payment.showDetails;
      
      if (payment.showDetails && !payment.details) {
        await this.fetchPaymentDetails(payment);
      }
    },
    async fetchPaymentDetails(payment) {
      if (payment.loadingDetails) return;

      payment.loadingDetails = true;
      try {
        if (this.isDirectExpensePayment(payment)) {
          await this.fetchDirectExpenseDetails(payment);
          return;
        }

        let payBillRecords = [];
        
        // Check if records are already loaded in the payment object
        if (payment.records && Array.isArray(payment.records) && payment.records.length > 0) {
          logger.log('Using existing records from payment object');
          payBillRecords = payment.records;
        } else {
          // Fetch pay bill records from API
          logger.log('Fetching records from API for payment:', payment.id);
          const response = await ApiService.get(`/vendor/pay-bill/${payment.id}/records`)
            .then(res => res.data)
            .catch(err => {
              logger.error('Error fetching records:', err);
              return [];
            });
          
          // API returns an array directly, not wrapped in an object
          payBillRecords = Array.isArray(response) ? response : [];
        }
        
        logger.log('Pay bill records for payment:', payBillRecords.length);

        const recordsWithDetails = this.groupPayBillRecordsByBill(
          payBillRecords.map((payRecord) => {
            const recordData = payRecord.toJSON ? payRecord.toJSON() : payRecord;
            return {
              ...recordData,
              showDetails: false,
              loadingDetails: false,
              billDetails: null,
              bill: recordData.bill || payRecord.bill || null,
              amountPaid: recordData.amountPaid || payRecord.amountPaid || 0,
            };
          })
        );

        payment.details = {
          payBillRecords: recordsWithDetails,
        };
        
        logger.log('Payment details set:', payment.details);
      } catch (error) {
        logger.error('Error fetching payment details:', error);
        payment.details = {
          payBillRecords: []
        };
      } finally {
        payment.loadingDetails = false;
      }
    },
    async fetchDirectExpenseDetails(payment) {
      const endpoint = payment.paymentSourceType === 'cash-expense'
        ? `/vendor/cash-expense/${payment.id}`
        : `/vendor/credit-card-expense/${payment.id}`;

      const response = await ApiService.get(endpoint).catch((err) => {
        logger.error('Error fetching direct expense details:', err);
        return { data: null };
      });

      const expense = response.data || null;
      if (expense?.payFromAccount) {
        payment.payFromAccount = expense.payFromAccount;
      }

      payment.details = {
        isDirectExpense: true,
        expenseKind: payment.expenseKind,
        accountRecords: Array.isArray(expense?.accountRecords) ? expense.accountRecords : [],
        itemRecords: Array.isArray(expense?.itemRecords) ? expense.itemRecords : [],
      };
    },
    groupPayBillRecordsByBill(payBillRecords) {
      const groups = new Map();

      for (const payRecord of payBillRecords) {
        const billId = payRecord.billId || payRecord.bill?.id;
        if (!billId) continue;

        if (!groups.has(String(billId))) {
          groups.set(String(billId), {
            ...payRecord,
            billId,
            bill: payRecord.bill || null,
            amountPaid: 0,
            allocations: [],
            showDetails: false,
            loadingDetails: false,
            billDetails: null,
          });
        }

        const group = groups.get(String(billId));
        group.amountPaid += Number(payRecord.amountPaid) || 0;
        group.allocations.push({
          id: payRecord.id,
          recordId: payRecord.recordId,
          recordType: payRecord.recordType,
          amountPaid: payRecord.amountPaid,
        });

        if (payRecord.bill && !group.bill) {
          group.bill = payRecord.bill;
        } else if (payRecord.bill?.accountRecords?.length && !group.bill?.accountRecords?.length) {
          group.bill = payRecord.bill;
        }
      }

      return Array.from(groups.values());
    },
    filterBillDetailsForPayment(billDetails, allocations) {
      const typed = (allocations || []).filter(
        (row) => row.recordId != null && row.recordId !== '' && row.recordType
      );
      if (!typed.length) {
        return billDetails;
      }

      const consultantIds = new Set(
        typed.filter((row) => row.recordType === 'consultant').map((row) => String(row.recordId))
      );
      const accountIds = new Set(
        typed.filter((row) => row.recordType === 'account').map((row) => String(row.recordId))
      );
      const itemIds = new Set(
        typed.filter((row) => row.recordType === 'item').map((row) => String(row.recordId))
      );

      return {
        accountRecords: accountIds.size
          ? (billDetails.accountRecords || []).filter((row) => accountIds.has(String(row.id)))
          : [],
        itemRecords: itemIds.size
          ? (billDetails.itemRecords || []).filter((row) => itemIds.has(String(row.id)))
          : [],
        consultantRecords: consultantIds.size
          ? (billDetails.consultantRecords || []).filter((row) => consultantIds.has(String(row.id)))
          : [],
      };
    },
    async togglePaidBillDetails(payRecord) {
      payRecord.showDetails = !payRecord.showDetails;

      if (payRecord.showDetails && !payRecord.billDetails && payRecord.bill?.id) {
        await this.fetchBillDetails(payRecord);
      }
    },
    async fetchBillDetails(payRecord) {
      if (payRecord.loadingDetails || !payRecord.bill?.id) return;
      
      payRecord.loadingDetails = true;
      try {
        // Check if bill details are already loaded in the bill object
        let accountRecords = [];
        let itemRecords = [];
        let consultantRecords = [];
        
        if (payRecord.bill && payRecord.bill.accountRecords && payRecord.bill.accountRecords.length > 0) {
          logger.log('Using existing bill details from payRecord');
          accountRecords = payRecord.bill.accountRecords || [];
          itemRecords = payRecord.bill.itemRecords || [];
          consultantRecords = payRecord.bill.consultantRecords || [];
        } else {
          // Fetch all three types of records in parallel
          logger.log('Fetching bill details from API for bill:', payRecord.bill.id);
          const [accounts, items, consultants] = await Promise.all([
            ApiService.get(`/vendor/enter-bill/${payRecord.bill.id}/accounts`).then(res => res.data).catch(() => []),
            ApiService.get(`/vendor/enter-bill/${payRecord.bill.id}/items`).then(res => res.data).catch(() => []),
            ApiService.get(`/vendor/enter-bill/${payRecord.bill.id}/consultants`).then(res => res.data).catch(() => [])
          ]);
          
          accountRecords = accounts || [];
          itemRecords = items || [];
          consultantRecords = consultants || [];
        }
        
        payRecord.billDetails = this.filterBillDetailsForPayment(
          {
            accountRecords: Array.isArray(accountRecords) ? accountRecords : [],
            itemRecords: Array.isArray(itemRecords) ? itemRecords : [],
            consultantRecords: Array.isArray(consultantRecords) ? consultantRecords : [],
          },
          payRecord.allocations
        );
        
        logger.log('Bill details set:', payRecord.billDetails);
      } catch (error) {
        logger.error('Error fetching bill details:', error);
        payRecord.billDetails = this.filterBillDetailsForPayment(
          {
            accountRecords: [],
            itemRecords: [],
            consultantRecords: [],
          },
          payRecord.allocations
        );
      } finally {
        payRecord.loadingDetails = false;
      }
    },
    formatDirectExpenseAccount(account) {
      if (!account) return '-';
      if (account.accountName && account.accountNumber) {
        return `${account.accountNumber} - ${account.accountName}`;
      }
      const mapped = account.accountId ? this.accountsMap[account.accountId] : null;
      if (mapped) {
        const num = mapped.accountNumber || '';
        const name = mapped.accountName || '';
        return num ? `${num} - ${name}` : name || '-';
      }
      return account.accountName || '-';
    },
    formatAccountName(account) {
      if (!account) return 'Account';
      if (account.account) {
        const accountNumber = account.account.accountNumber || '';
        const accountName = account.account.accountName || '';
        return accountNumber ? `${accountNumber} - ${accountName}` : accountName;
      }
      return account.accountName || account.description || 'Account';
    },
    formatPayFromAccount(account) {
      if (!account) return '-';
      const accountNumber = account.accountNumber || '';
      const accountName = account.accountName || '';
      return accountNumber ? `${accountNumber} - ${accountName}` : accountName || '-';
    },
    getVendorName(payment) {
      if (payment.vendorName) {
        return payment.vendorName;
      }
      if (payment.vendor) {
        return payment.vendor.vendorName || payment.vendor.name || 'Unknown Vendor';
      }
      if (payment.vendorId) {
        const vendor = this.vendors.find((v) => String(v.id) === String(payment.vendorId));
        if (vendor) {
          return vendor.vendorName || vendor.name || 'Unknown Vendor';
        }
      }
      return 'Unknown Vendor';
    },
    getPaymentMethod(payment) {
      if (this.isDirectExpensePayment(payment)) {
        return payment.paymentMethod || (payment.paymentSourceType === 'cash-expense' ? 'Cash' : 'Credit Card');
      }
      return payment.paymentMethod || '-';
    },
    getAccountName(payment) {
      if (payment.payFromAccount) {
        const account = payment.payFromAccount;
        const label = account.accountNumber
          ? `${account.accountNumber} - ${account.accountName}`
          : account.accountName || '-';
        return account.accountType ? `${label} (${account.accountType})` : label;
      }
      if (payment.payFrom && this.accountsMap[payment.payFrom]) {
        const account = this.accountsMap[payment.payFrom];
        const label = account.accountNumber
          ? `${account.accountNumber} - ${account.accountName}`
          : account.accountName || '-';
        return account.accountType ? `${label} (${account.accountType})` : label;
      }
      return '-';
    },
    handleExportCSV() {
      this.$toast && this.$toast.info('Export to CSV coming soon!');
    },
    handleUpdate() {
      this.appliedDateType = this.dateType || 'All';
      this.appliedFromDate = this.fromDate || '';
      this.appliedToDate = this.toDate || '';
      this.currentPage = 1;
    },
    handlePageChange(page) {
      this.currentPage = page;
    },
    handleItemsPerPageChange(itemsPerPage) {
      this.itemsPerPage = itemsPerPage;
      this.currentPage = 1;
    }
  }
};
</script> 